import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { askChatbot } from "../../services/chatbotService";
import peacockIcon from "../../assets/peacock_ai_premium.png";
import "./Chatbot.css";

const QUICK_ACTIONS = [
  "My leave balance",
  "My assets",
  "Upcoming holidays",
  "My pending leaves",
  "Team on leave today",
];

/**
 * Floating AI Chatbot Widget
 *
 * A glassmorphism-styled chatbot panel anchored to the bottom-right of the
 * viewport.  Communicates with the Mistral-powered backend endpoint.
 */
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [isOpen]);

  const addMessage = (role, text) => {
    setMessages((prev) => [...prev, { role, text, id: Date.now() + Math.random() }]);
  };

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isLoading) return;

    // Prepare history for the API (Mistral expects 'user' and 'assistant' roles)
    const formattedHistory = messages.map(msg => ({
      role: msg.role === "bot" ? "assistant" : "user",
      content: msg.text
    }));

    addMessage("user", trimmed);
    setInput("");
    setIsLoading(true);

    try {
      const data = await askChatbot(trimmed, formattedHistory);
      addMessage("bot", data.response || "I couldn't process that. Please try again.");
    } catch (err) {
      console.error("Chatbot error:", err);
      if (err.response?.status === 429) {
        addMessage("bot", "You're asking too many questions. Please wait a moment and try again.");
      } else {
        addMessage(
          "bot",
          "I'm having trouble connecting right now. Please try again shortly."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const togglePanel = () => setIsOpen((prev) => !prev);

  return (
    <>
      {/* ── Chat Panel ─────────────────────────────────────────────── */}
      <div className={`chatbot-panel ${isOpen ? "visible" : ""}`}>
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-avatar">
            <img src={peacockIcon} className="peacock-ai-icon" alt="WorkSphere AI" />
          </div>
          <div className="chatbot-header-info">
            <div className="chatbot-header-title">WorkSphere AI</div>
            <div className="chatbot-header-subtitle">
              <span className="chatbot-status-dot" />
              Online · Ask me anything
            </div>
          </div>
          <button className="chatbot-close-btn" onClick={togglePanel} aria-label="Close chatbot">
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.length === 0 && !isLoading && (
            <div className="chatbot-welcome">
              <div className="chatbot-welcome-icon">
                <img src={peacockIcon} className="peacock-ai-icon welcome-icon" alt="Welcome" />
              </div>
              <h4>Hi there! 👋</h4>
              <p>
                I can help you check your leave balance, view assets,
                find upcoming holidays, and more. Just ask!
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`chatbot-msg ${msg.role}`}>
              <div className="chatbot-msg-avatar">
                {msg.role === "bot" ? (
                  <img src={peacockIcon} className="peacock-ai-icon msg-avatar" alt="AI" />
                ) : (
                  <span className="material-symbols-rounded">person</span>
                )}
              </div>
              <div className="chatbot-msg-bubble">
                {msg.role === "bot" ? (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                ) : (
                  msg.text
                )}
              </div>
            </div>
          ))}


          {isLoading && (
            <div className="chatbot-typing">
              <div className="chatbot-msg-avatar" style={{
                background: "linear-gradient(135deg, #0d9488, #065f46)",
                width: 28, height: 28, borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <img src={peacockIcon} className="peacock-ai-icon msg-avatar" alt="AI Typing" />
              </div>
              <div className="chatbot-typing-bubble">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions — shown only when no messages yet */}
        {messages.length === 0 && !isLoading && (
          <div className="chatbot-quick-actions">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action}
                className="chatbot-quick-btn"
                onClick={() => sendMessage(action)}
              >
                {action}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="chatbot-input-area">
          <input
            ref={inputRef}
            className="chatbot-input"
            type="text"
            placeholder="Ask about leaves, assets, holidays..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            maxLength={500}
          />
          <button
            className="chatbot-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            aria-label="Send message"
          >
            <span className="material-symbols-rounded">send</span>
          </button>
        </div>
      </div>

      {/* ── Floating Action Button ─────────────────────────────────── */}
      <button
        className={`chatbot-fab ${isOpen ? "open" : ""}`}
        onClick={togglePanel}
        aria-label={isOpen ? "Close assistant" : "Open AI assistant"}
      >
        {isOpen ? (
          <span className="material-symbols-rounded fab-icon">close</span>
        ) : (
          <img src={peacockIcon} className="peacock-ai-icon fab-icon" alt="Open AI Assistant" />
        )}
      </button>
    </>
  );
};

export default Chatbot;

